import { useState } from 'react';
import { Smartphone, X, Apple, Chrome } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function PWAInstallGuide() {
  const [isOpen, setIsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('pwa-guide-dismissed') === 'true';
  });

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-guide-dismissed', 'true');
  };

  if (dismissed) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 bg-[#5C0120] text-white p-3 rounded-full shadow-lg hover:bg-[#5C0120]/90 transition-colors z-40"
        title="Install App"
      >
        <Smartphone className="h-5 w-5" />
      </button>
    );
  }

  return (
    <>
      {/* Floating button when guide is closed */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 bg-[#5C0120] text-white px-4 py-2 rounded-full shadow-lg hover:bg-[#5C0120]/90 transition-colors z-40 flex items-center gap-2"
        >
          <Smartphone className="h-4 w-4" />
          <span className="text-sm font-medium">Add to Phone</span>
        </button>
      )}

      {/* Install Guide Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-stone-100 flex items-center justify-between">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-[#5C0120]" />
                Add to Your Phone
              </h3>
              <button onClick={() => setIsOpen(false)} className="text-stone-400 hover:text-stone-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-6">
              {/* iPhone Instructions */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-stone-900 font-medium">
                  <Apple className="h-5 w-5" />
                  iPhone / iPad (Safari)
                </div>
                <ol className="space-y-2 text-sm text-stone-600 ml-7">
                  <li className="flex items-start gap-2">
                    <span className="bg-[#5C0120] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                    <span>Open this website in <strong>Safari</strong> (not Instagram or Facebook browser)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-[#5C0120] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                    <span>Tap the <strong>Share button</strong> (square with arrow pointing up) at the bottom of the screen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-[#5C0120] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                    <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-[#5C0120] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">4</span>
                    <span>Tap <strong>"Add"</strong> in the top right corner</span>
                  </li>
                </ol>
              </div>

              {/* Android Instructions */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-stone-900 font-medium">
                  <Chrome className="h-5 w-5" />
                  Android (Chrome)
                </div>
                <ol className="space-y-2 text-sm text-stone-600 ml-7">
                  <li className="flex items-start gap-2">
                    <span className="bg-[#5C0120] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                    <span>Open this website in <strong>Chrome</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-[#5C0120] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                    <span>Tap the <strong>three dots menu</strong> (top right corner)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-[#5C0120] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                    <span>Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-[#5C0120] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">4</span>
                    <span>Tap <strong>"Add"</strong> to confirm</span>
                  </li>
                </ol>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
                <strong>Important:</strong> Make sure you're using Safari (iPhone) or Chrome (Android), not the browser inside Instagram, Facebook or other apps.
              </div>
            </div>

            <div className="p-4 border-t border-stone-100 flex gap-2">
              <Button variant="outline" onClick={() => setIsOpen(false)} className="flex-1">
                Close
              </Button>
              <Button onClick={handleDismiss} className="flex-1 bg-[#5C0120] hover:bg-[#5C0120]/90">
                Don't Show Again
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PWAInstallGuide;
